import {
	getActiveSurfacePreset,
	type ActiveSurfaceMode,
	type ActiveSurfacePresetId,
	type SurfaceGlowConfig,
} from '@Shelly/shelly-design-system';
import type { DeviceOnOffState } from 'src/helpers/device_control_on_off';
import type { Device } from 'src/types/Device';

export type DashDeviceActiveSurfaceOverride =
	| ActiveSurfacePresetId
	| {
			preset?: ActiveSurfacePresetId;
			mode?: ActiveSurfaceMode;
			surfaceGlow?: SurfaceGlowConfig;
			accentColor?: string;
			backgroundColor?: string;
	  };

export type ResolveDashDeviceActiveSurfaceInput = {
	device: Device | undefined | null;
	status?: any;
	settings?: any;
	onOffState: DeviceOnOffState | null | undefined;
	/** When true, apply compact gray `device-state` glow. */
	offline?: boolean;
	/**
	 * Runtime device-state type from `compute-device-state` (0 offline, 4 calibrating,
	 * 7 firmware required, 15 missing component, …). `99` / default = normal.
	 */
	deviceStateType?: number;
	/** External override (gallery / Customize Card / future widget config). */
	override?: DashDeviceActiveSurfaceOverride;
	/**
	 * When true and a usable light color exists, lights / Cury may auto-pick
	 * `light-color` glow. Default true.
	 */
	enableLightColor?: boolean;
	/** Optional light color (hex/oklch/css) when enableLightColor or light-color preset. */
	lightColor?: string | null;
};

export type ResolveDashDeviceActiveSurfaceResult = {
	active: boolean;
	activeSurfaceMode: ActiveSurfaceMode;
	surfaceGlow: SurfaceGlowConfig;
	presetId: ActiveSurfacePresetId | 'custom';
};

/** Type regexes mirrored from interface_helpers — kept local to avoid vitest mock coupling. */
const FLOOD_TYPES = /^(SHWT-1|SBWD-1|SBFL-1|SHFL-1|S4SN-0071A|S4SN-0071Z)$/;
const SMOKE_TYPES = /^(SHGS-1|SNSN-0031Z|S4SN-0031Z|S3SN-0082A)$/;
const GAS_TYPES = /^(SHGS-1)$/;
const HT_TYPES =
	/^(SHHT-1|SHTC-1|S3SN-0013A|SBHT-002C|SBWT-002C|SNSN-0013A|S3SN-0U12A|SBHT-003C|SBHT-103C|SBHT-203C|HT1NB|S3SN-1U12A)$/;
/** Motion / motion-like devices — compact glow footprint. */
const MOTION_TYPES = /^(SHMOS-01|SHMOS-02|SBMO-003Z|SBMO-103Z|SHSEN-1|S4SN-0U61X|S4SN-0A61X)$/;
/** Door/window — same compact glow as motion. */
const DOOR_WINDOW_TYPES = /^(SHDW-1|SHDW-2|SBDW-002C|SBDW-002D|SBDW-103C|SBDW-203C|SBDW-302C)$/;
const CURY_TYPES = /^(S4PB-00CU000002)$/;
/** Relays that must never pick sensor glow (always opaque white when active). */
const FORCE_WHITE_ACTIVE_TYPES = /^(SNSW-001X16EU|SNSW-001P16EU)$/;

/** Compact HT sizes — temperature colour stays, footprint stays modest. */
const HT_GLOW_SIZE_OVERRIDES = {
	active: {
		interiorStrength: 0.7,
		edgeStrength: 0.76,
		interiorSize: 120,
		edgeSize: 156,
	},
	inactive: {
		interiorStrength: 0.28,
		edgeStrength: 0.4,
		interiorSize: 84,
		edgeSize: 112,
	},
} as const;

/**
 * TemperatureWheel white-balance stops (warm → cool), used for CCT → glow colour.
 * Source: `TemperatureWheelGradients.svelte` `type="white-balance"`.
 */
const WHITE_BALANCE_STOPS: Array<{ t: number; rgb: [number, number, number] }> = [
	{ t: 0, rgb: [255, 169, 68] }, // #FFA944
	{ t: 0.25, rgb: [255, 194, 122] }, // #FFC27A
	{ t: 0.4, rgb: [255, 226, 191] }, // #FFE2BF
	{ t: 0.55, rgb: [254, 254, 254] }, // #FEFEFE
	{ t: 0.75, rgb: [218, 230, 255] }, // #DAE6FF
	{ t: 0.9, rgb: [164, 195, 255] }, // #A4C3FF
	{ t: 1, rgb: [155, 189, 255] }, // #9BBDFF
];

const isAlertSensorType = (type: string): boolean =>
	FLOOD_TYPES.test(type) || SMOKE_TYPES.test(type) || GAS_TYPES.test(type);

export const isDashHtDeviceType = (type: string | undefined): boolean => !!type && HT_TYPES.test(type);

export const isDashMotionDeviceType = (type: string | undefined): boolean =>
	!!type && MOTION_TYPES.test(type);

export const isDashDoorWindowDeviceType = (type: string | undefined): boolean =>
	!!type && DOOR_WINDOW_TYPES.test(type);

export const isDashCuryDeviceType = (type: string | undefined): boolean =>
	!!type && CURY_TYPES.test(type);

const prefersOpaqueWhiteActive = (device: Device | undefined | null): boolean => {
	if (!device?.type) return false;
	if (FORCE_WHITE_ACTIVE_TYPES.test(device.type)) return true;
	return device.category === 'relay';
};

/**
 * Read temperature in °C from common Gen1 / Gen2+ status shapes.
 */
export const getDashDeviceTemperatureC = (status: any): number | null => {
	if (!status) return null;

	const gen2 = status['temperature:0']?.tC;
	if (typeof gen2 === 'number' && !Number.isNaN(gen2)) return gen2;

	const tmp = status?.tmp;
	if (typeof tmp?.value === 'number' && tmp.value !== 999 && !Number.isNaN(tmp.value)) {
		if (tmp.units === 'F') return ((tmp.value - 32) * 5) / 9;
		return tmp.value;
	}

	return null;
};

export type HtTemperatureGlow = {
	accentColor: string;
	/** Stronger glow outside the comfort band (cold / hot). */
	active: boolean;
};

/**
 * Semantic HT glow: blue when cool, red when warm/hot.
 * Comfort (~18–24 °C) stays subtle idle; extremes assert stronger glow.
 */
export const resolveHtTemperatureGlow = (temperatureC: number): HtTemperatureGlow => {
	if (temperatureC < 18) {
		return {
			accentColor: temperatureC <= 14 ? '--color-primary' : '--color-lightblue',
			active: temperatureC < 16,
		};
	}
	if (temperatureC > 24) {
		return {
			accentColor: temperatureC >= 28 ? '--color-heating' : '--color-danger',
			active: temperatureC > 26,
		};
	}
	return { accentColor: '--color-lightblue', active: false };
};

/**
 * Map CCT kelvin onto the TemperatureWheel white-balance gradient.
 * Typical bulb range ~2700–6500 K.
 */
export const whiteBalanceKelvinToCssRgb = (kelvin: number): string => {
	const minK = 2700;
	const maxK = 6500;
	const t = Math.min(1, Math.max(0, (kelvin - minK) / (maxK - minK)));

	let left = WHITE_BALANCE_STOPS[0];
	let right = WHITE_BALANCE_STOPS[WHITE_BALANCE_STOPS.length - 1];
	for (let i = 0; i < WHITE_BALANCE_STOPS.length - 1; i++) {
		if (t >= WHITE_BALANCE_STOPS[i].t && t <= WHITE_BALANCE_STOPS[i + 1].t) {
			left = WHITE_BALANCE_STOPS[i];
			right = WHITE_BALANCE_STOPS[i + 1];
			break;
		}
	}

	const span = right.t - left.t || 1;
	const localT = (t - left.t) / span;
	const mix = (a: number, b: number) => Math.round(a + (b - a) * localT);
	const r = mix(left.rgb[0], right.rgb[0]);
	const g = mix(left.rgb[1], right.rgb[1]);
	const b = mix(left.rgb[2], right.rgb[2]);
	return `rgb(${r}, ${g}, ${b})`;
};

const rgbTupleToCss = (rgb: number[] | undefined | null): string | null => {
	if (!Array.isArray(rgb) || rgb.length < 3) return null;
	const [r, g, b] = rgb;
	if (![r, g, b].every((c) => typeof c === 'number' && Number.isFinite(c))) return null;
	return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const scaleGlowSizesByBrightness = (
	surfaceGlow: SurfaceGlowConfig,
	brightnessPercent: number,
): SurfaceGlowConfig => {
	// Ambient / light brightness is 0–100. Map to a clear radius range so low
	// strength stays compact and full strength fills more of the card.
	const factor = clamp01(brightnessPercent / 100);
	const minFactor = 0.28;
	const scale = minFactor + (1 - minFactor) * factor;
	const active = surfaceGlow.active ?? {};
	const baseInterior = active.interiorSize ?? 150;
	const baseEdge = active.edgeSize ?? 190;
	return {
		...surfaceGlow,
		active: {
			...active,
			interiorSize: Math.round(baseInterior * scale),
			edgeSize: Math.round(baseEdge * scale),
			interiorStrength: (active.interiorStrength ?? 0.86) * (0.45 + 0.55 * factor),
			edgeStrength: (active.edgeStrength ?? 0.8) * (0.45 + 0.55 * factor),
		},
	};
};

/**
 * Cury ambient colour from settings (`cury:N.ambient.color`).
 */
export const resolveCuryAmbientLightColor = (
	device: Device | undefined | null,
	status: any,
	settings: any,
): { color: string; active: boolean; brightness: number } | null => {
	if (!isDashCuryDeviceType(device?.type) || !settings) return null;

	const channel = typeof device?.channel === 'number' ? device.channel : 0;
	const key = `cury:${channel}`;
	const ambient = settings?.[key]?.ambient ?? settings?.['cury:0']?.ambient;
	if (!ambient) return null;

	const color = rgbTupleToCss(ambient.color);
	if (!color) return null;

	const statusBlock = status?.[key] ?? status?.['cury:0'];
	const active =
		ambient.enable === true || !!statusBlock?.output || !!statusBlock?.on || !!statusBlock?.ison;

	const brightness =
		typeof ambient.brightness === 'number' && Number.isFinite(ambient.brightness)
			? Math.max(0, Math.min(100, ambient.brightness))
			: typeof statusBlock?.ambient?.brightness === 'number'
				? Math.max(0, Math.min(100, statusBlock.ambient.brightness))
				: 100;

	return { color, active, brightness };
};

const isLikelyLightDevice = (device: Device | undefined | null, status: any): boolean => {
	if (!device) return false;
	const category = device.category;
	if (category === 'light' || category === 'lights' || category === 'rgbw' || category === 'bulb') {
		return true;
	}
	if (device.mode === 'light' || device.mode === 'white' || device.mode === 'color') {
		return true;
	}
	if (!status) return false;
	if (Array.isArray(status.lights)) return true;
	return Object.keys(status).some((key) => /^(light|rgb|rgbw|rgbcct|cct):/.test(key));
};

/**
 * Resolve colour + brightness for light devices (RGB / CCT / white).
 */
export const resolveDashLightSurfaceColor = (
	device: Device | undefined | null,
	status: any,
	settings: any,
): { color: string; brightness: number; mode: 'color' | 'white' } | null => {
	if (!device || !status || !isLikelyLightDevice(device, status)) return null;
	if (prefersOpaqueWhiteActive(device)) return null;

	const channel = typeof device.channel === 'number' ? device.channel : 0;

	const candidates = [
		status[`rgbw:${channel}`],
		status[`rgb:${channel}`],
		status[`rgbcct:${channel}`],
		status[`cct:${channel}`],
		status[`light:${channel}`],
		status['rgbw:0'],
		status['rgb:0'],
		status['rgbcct:0'],
		status['light:0'],
	].filter(Boolean);

	for (const block of candidates) {
		const brightness =
			typeof block.brightness === 'number'
				? block.brightness
				: typeof block.gain === 'number'
					? block.gain
					: 100;

		if (Array.isArray(block.rgb) && block.rgb.length >= 3) {
			const color = rgbTupleToCss(block.rgb);
			if (color) return { color, brightness, mode: 'color' };
		}

		const ct =
			typeof block.ct === 'number'
				? block.ct
				: typeof block.temp === 'number'
					? block.temp
					: typeof block.color_temp === 'number'
						? block.color_temp
						: null;
		if (ct != null) {
			return { color: whiteBalanceKelvinToCssRgb(ct), brightness, mode: 'white' };
		}
	}

	const gen1 = status?.lights?.[channel];
	if (gen1) {
		const brightness =
			typeof gen1.brightness === 'number'
				? gen1.brightness
				: typeof gen1.gain === 'number'
					? gen1.gain
					: 100;
		if (
			typeof gen1.red === 'number' &&
			typeof gen1.green === 'number' &&
			typeof gen1.blue === 'number'
		) {
			return {
				color: `rgb(${gen1.red}, ${gen1.green}, ${gen1.blue})`,
				brightness,
				mode: 'color',
			};
		}
		const ct = typeof gen1.temp === 'number' ? gen1.temp : gen1.ct;
		if (typeof ct === 'number') {
			return { color: whiteBalanceKelvinToCssRgb(ct), brightness, mode: 'white' };
		}
	}

	return null;
};

/**
 * Binary “asserted” state for sensors without a writable on/off control.
 * Returns false when the status shape is unknown (e.g. HT comfort band — handled separately).
 */
export const getDashSensorAssertedState = (
	device: { type?: string; channel?: number } | null | undefined,
	status: any,
): boolean => {
	if (!device?.type || !status) return false;

	const channel = typeof device.channel === 'number' ? device.channel : 0;
	const windowKey = `window:${channel}`;
	if (status[windowKey] && typeof status[windowKey].open === 'boolean') {
		return !!status[windowKey].open;
	}
	if (status['window:0'] && typeof status['window:0'].open === 'boolean') {
		return !!status['window:0'].open;
	}

	const sensorState = status?.sensor?.state;
	if (sensorState === 'open' || sensorState === 'close' || sensorState === 'closed') {
		return sensorState === 'open';
	}

	if (typeof status.motion === 'boolean') return status.motion;
	if (status['motion:0'] && typeof status['motion:0'].motion === 'boolean') {
		return !!status['motion:0'].motion;
	}

	if (typeof status.flood === 'boolean') return status.flood;
	if (status['flood:0'] && typeof status['flood:0'].flood === 'boolean') {
		return !!status['flood:0'].flood;
	}

	if (typeof status.smoke === 'boolean') return status.smoke;
	if (status['smoke:0']) {
		return !!(status['smoke:0'].alarm || status['smoke:0'].smoke);
	}

	const gasAlarm = status?.gas_sensor?.alarm_state;
	if (typeof gasAlarm === 'string' && gasAlarm !== 'none') return true;

	const presenceKeys = Object.keys(status).filter((key) => key.startsWith('presencezone:'));
	if (presenceKeys.length > 0) {
		return presenceKeys.some((key) => {
			const zone = status[key];
			if (!zone) return false;
			if (zone.value) return true;
			return typeof zone.num_objects === 'number' && zone.num_objects > 0;
		});
	}

	return false;
};

const hasNoMainToggleAction = (onOffState: DeviceOnOffState | null | undefined): boolean =>
	!onOffState || !!onOffState.readOnly;

/** Default “live” state type used by device-runtime (see `defaultStateType`). */
const DEFAULT_DEVICE_STATE_TYPE = 99;

/**
 * Compact saved/blocking-state glow colors — shared geometry via `device-state` preset.
 * Only the accent changes per status.
 */
export const resolveDeviceStateGlowAccent = (
	offline: boolean,
	deviceStateType: number | undefined,
): string | null => {
	if (offline || deviceStateType === 0) return '--color-3';
	if (typeof deviceStateType !== 'number' || deviceStateType === DEFAULT_DEVICE_STATE_TYPE) {
		return null;
	}

	switch (deviceStateType) {
		case 7: // firmware required / unsupported
		case 4: // calibrating
		case 15: // missing component / sensor
		case 1: // pending connection
			return '--color-warning';
		case 5: // locked
		case 6: // pin locked
		case 9: // unauthorized
		case 14: // disabled
		case 2: // rebooting
		case 3: // updating
		case 8: // loading
		case 10: // updating wifi
		case 11: // associating
			return '--color-3';
		default:
			return '--color-3';
	}
};

const pickDefaultPresetId = (
	device: Device | undefined | null,
	onOffState: DeviceOnOffState | null | undefined,
	enableLightColor: boolean,
	lightColor?: string | null,
): ActiveSurfacePresetId => {
	if (hasNoMainToggleAction(onOffState)) {
		// Relays / Plus 1 / Plus 1PM must never inherit sensor glass glow.
		if (prefersOpaqueWhiteActive(device)) return 'white';
		if (device?.type && isAlertSensorType(device.type)) return 'sensor-alert';
		if (isDashMotionDeviceType(device?.type) || isDashDoorWindowDeviceType(device?.type)) {
			return 'sensor-motion';
		}
		return 'sensor';
	}

	if (enableLightColor && lightColor) return 'light-color';
	return 'white';
};

const resolveOverride = (
	override: DashDeviceActiveSurfaceOverride | undefined,
	lightColor?: string | null,
): ResolveDashDeviceActiveSurfaceResult | null => {
	if (!override) return null;

	if (typeof override === 'string') {
		const preset = getActiveSurfacePreset(
			override,
			override === 'light-color' && lightColor
				? { accentColor: lightColor, spotColor: lightColor, edgeColor: lightColor }
				: {},
		);
		return {
			active: false,
			activeSurfaceMode: preset.mode,
			surfaceGlow: preset.surfaceGlow,
			presetId: override,
		};
	}

	if (override.preset) {
		const preset = getActiveSurfacePreset(override.preset, {
			accentColor: override.accentColor,
			backgroundColor: override.backgroundColor ?? lightColor ?? undefined,
			spotColor: override.accentColor,
		});
		return {
			active: false,
			activeSurfaceMode: override.mode ?? preset.mode,
			surfaceGlow: override.surfaceGlow
				? { ...preset.surfaceGlow, ...override.surfaceGlow }
				: preset.surfaceGlow,
			presetId: override.preset,
		};
	}

	if (override.surfaceGlow) {
		return {
			active: false,
			activeSurfaceMode: override.mode ?? 'opaque',
			surfaceGlow: override.surfaceGlow,
			presetId: 'custom',
		};
	}

	return null;
};

/**
 * Resolve active face mode + lighting preset for dash device widgets.
 * Writable Quick Control defaults to `white`; sensors get glass + idle/asserted glow.
 * Lights / Cury use coloured glow behind the avatar when a colour is available.
 */
export const resolveDashDeviceActiveSurface = (
	input: ResolveDashDeviceActiveSurfaceInput,
): ResolveDashDeviceActiveSurfaceResult => {
	const {
		device,
		status,
		settings,
		onOffState,
		offline = false,
		deviceStateType,
		override,
		enableLightColor = true,
		lightColor: lightColorProp,
	} = input;

	const overridden = resolveOverride(override, lightColorProp);
	if (overridden) {
		let active = false;
		if (onOffState && !onOffState.readOnly) active = !!onOffState.isOn;
		else if (onOffState?.readOnly) active = !!onOffState.isOn;
		else active = getDashSensorAssertedState(device, status);
		return { ...overridden, active };
	}

	const stateAccent = resolveDeviceStateGlowAccent(offline, deviceStateType);
	if (stateAccent) {
		const preset = getActiveSurfacePreset('device-state', {
			accentColor: stateAccent,
			spotColor: stateAccent,
			edgeColor: stateAccent,
		});
		return {
			active: false,
			activeSurfaceMode: preset.mode,
			surfaceGlow: preset.surfaceGlow,
			presetId: 'device-state',
		};
	}

	// Cury — ambient colour glow behind the avatar
	const curyAmbient = resolveCuryAmbientLightColor(device, status, settings);
	if (curyAmbient && enableLightColor) {
		const preset = getActiveSurfacePreset('light-color', {
			accentColor: curyAmbient.color,
			spotColor: curyAmbient.color,
			edgeColor: curyAmbient.color,
			hotspotTarget: 'avatar',
		});
		return {
			active: curyAmbient.active,
			activeSurfaceMode: preset.mode,
			surfaceGlow: scaleGlowSizesByBrightness(preset.surfaceGlow, curyAmbient.brightness),
			presetId: 'light-color',
		};
	}

	// Lights — RGB / CCT glow (TemperatureWheel palette for white), radius from brightness
	const lightSurface =
		enableLightColor && !prefersOpaqueWhiteActive(device)
			? lightColorProp
				? {
						color: lightColorProp,
						brightness:
							resolveDashLightSurfaceColor(device, status, settings)?.brightness ?? 100,
						mode: 'color' as const,
					}
				: resolveDashLightSurfaceColor(device, status, settings)
			: null;

	if (lightSurface && onOffState && !onOffState.readOnly) {
		const preset = getActiveSurfacePreset('light-color', {
			accentColor: lightSurface.color,
			spotColor: lightSurface.color,
			edgeColor: lightSurface.color,
			hotspotTarget: 'avatar',
		});
		return {
			active: !!onOffState.isOn,
			activeSurfaceMode: preset.mode,
			surfaceGlow: scaleGlowSizesByBrightness(preset.surfaceGlow, lightSurface.brightness),
			presetId: 'light-color',
		};
	}

	const lightColor = lightColorProp ?? null;
	const presetId = pickDefaultPresetId(device, onOffState, enableLightColor, lightColor);

	const htGlow =
		hasNoMainToggleAction(onOffState) && isDashHtDeviceType(device?.type)
			? (() => {
					const tC = getDashDeviceTemperatureC(status);
					return tC == null ? null : resolveHtTemperatureGlow(tC);
				})()
			: null;

	const colorOverrides = htGlow
		? {
				accentColor: htGlow.accentColor,
				spotColor: htGlow.accentColor,
				edgeColor: htGlow.accentColor,
				active: { ...HT_GLOW_SIZE_OVERRIDES.active },
				inactive: { ...HT_GLOW_SIZE_OVERRIDES.inactive },
			}
		: presetId === 'light-color' && lightColor
			? {
					accentColor: lightColor,
					spotColor: lightColor,
					edgeColor: lightColor,
				}
			: {};

	const preset = getActiveSurfacePreset(presetId, colorOverrides);

	let active = false;
	if (htGlow) {
		active = htGlow.active;
	} else if (onOffState && !onOffState.readOnly) {
		active = !!onOffState.isOn;
	} else if (onOffState?.readOnly) {
		active = !!onOffState.isOn;
	} else {
		active = getDashSensorAssertedState(device, status);
	}

	return {
		active,
		activeSurfaceMode: preset.mode,
		surfaceGlow: preset.surfaceGlow,
		presetId,
	};
};
